'use client';

import {
	BigNum,
	MarketType,
	OrderTriggerCondition,
	OrderType,
	PositionDirection,
} from '@drift-labs/sdk';
import { COMMON_UI_UTILS, ENUM_UTILS, MarketId, UIMarket } from '@drift/common';
import { Swap } from '@drift-labs/icons';
import { useCallback, useMemo } from 'react';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useMarketStepSize } from 'src/hooks/useMarketStepSize';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';
import { isEntirePositionOrder } from 'src/utils/fullPositionOrders';
import MobileTableCard from '../Tables/cards/MobileTableCard';
import Button from '../primitives/Button/Button';
import { roundToStepSizeIfLargeEnough } from 'src/utils/math';
import { AccountOrder } from './types';
import NumberDisplay from '../NumberDisplay/NumberDisplay';
import useModal from 'src/components/Modals/hooks/useModal';
import { BalanceDisplayType } from 'src/@types/types';
import Tag from 'src/components/primitives/Tag/Tag';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import useExpandableItem, {
	ExpandableItemParams,
} from 'src/hooks/lists/expandable/useExpandableItem';
import { useCancelOrders } from 'src/hooks/trade/cancel/useCancelOrders';

interface OpenPerpOrderMobileCardProps extends ExpandableItemParams {
	order: AccountOrder;
	showAccountValues: boolean;
	sizeDisplayType: BalanceDisplayType;
	onToggleSizeDisplayType: () => void;
}

export const OpenPerpOrderMobileCard = ({
	order,
	showAccountValues,
	areAllItemsExpanded,
	handleItemExpandedChange,
	sizeDisplayType,
	onToggleSizeDisplayType,
}: OpenPerpOrderMobileCardProps) => {
	const { show } = useModal();
	const { switchMarket } = useSwitchMarket();
	const getOraclePrice = useGetOraclePriceForMarket();
	const isSpot = ENUM_UTILS.match(order.marketType, MarketType.SPOT);
	const { cancelOrders } = useCancelOrders();

	const marketId = new MarketId(order.marketIndex, order.marketType);
	const stepSize = useMarketStepSize(marketId);
	const getTickSizeForMarket = useGetTickSizeForMarket();
	const tickSize = getTickSizeForMarket(marketId);
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);

	const { isExpanded, handleExpandedChange } = useExpandableItem({
		areAllItemsExpanded,
		handleItemExpandedChange,
	});

	const isMarketOrder =
		ENUM_UTILS.match(order.orderType, OrderType.MARKET) ||
		ENUM_UTILS.match(order.orderType, OrderType.ORACLE);
	const shouldShowEditButton = !isMarketOrder;

	const handleClickMarket = useCallback(
		(marketIndex: number, marketType: MarketType) => {
			switchMarket({
				marketIndex,
				marketType,
			});
		},
		[switchMarket]
	);

	const handleShowEditOrderModal = () => {
		show({ type: 'edit_order', props: { order } });
	};

	const uiMarket = new UIMarket(order.marketIndex, order.marketType);

	const triggerPriceToUse = order.triggerPrice;

	const triggerConditionAbove =
		ENUM_UTILS.match(order.triggerCondition, OrderTriggerCondition.ABOVE) ||
		ENUM_UTILS.match(
			order.triggerCondition,
			OrderTriggerCondition.TRIGGERED_ABOVE
		);

	const computedOrderLimitPrice = useMemo(() => {
		const oraclePrice = getOraclePrice(marketId) as BigNum;
		return COMMON_UI_UTILS.getLimitPriceFromOracleOffset(order, oraclePrice);
	}, [
		getOraclePrice,
		marketId.marketIndex,
		marketId.marketType,
		order.oraclePriceOffset?.toString(),
	]);

	const limitPriceToUse = computedOrderLimitPrice;

	const orderIsFullPosition = isEntirePositionOrder(order.baseAssetAmount);

	const marketName = isSpot
		? `${uiMarket.uiSymbols.baseAssetDisplaySymbol}-SPOT`
		: uiMarket.uiSymbols.baseAssetDisplaySymbol;

	const isLong = ENUM_UTILS.match(order.direction, PositionDirection.LONG);

	const sizeTextBase = showAccountValues
		? orderIsFullPosition
			? 'Entire Position'
			: COMMON_UI_UTILS.trimTrailingZeros(
					roundToStepSizeIfLargeEnough(
						order.baseAssetAmount.toTradePrecision(),
						stepSize
					)
				)
		: '∗∗∗∗∗';

	const filledTextBase = showAccountValues
		? COMMON_UI_UTILS.trimTrailingZeros(
				roundToStepSizeIfLargeEnough(
					order.baseAssetAmountFilled.toTradePrecision(),
					stepSize
				)
			)
		: '∗∗∗∗∗';

	const priceForNotional = useMemo(() => {
		if (!limitPriceToUse.eqZero()) return limitPriceToUse;
		const oracle = getOraclePrice(marketId) as BigNum;
		return oracle;
	}, [
		limitPriceToUse,
		getOraclePrice,
		marketId.marketIndex,
		marketId.marketType,
	]);

	const sizeText =
		sizeDisplayType === 'notional'
			? (() => {
					if (!showAccountValues) return '∗∗∗∗∗';
					if (orderIsFullPosition) return 'Entire Position';
					const notional = order.baseAssetAmount
						.mul(priceForNotional)
						.shift(order.baseAssetAmount.precision.neg());
					return notional.toNotional();
				})()
			: sizeTextBase;

	const filledText =
		sizeDisplayType === 'notional'
			? (() => {
					if (!showAccountValues) return '∗∗∗∗∗';
					const notional = order.baseAssetAmountFilled
						.mul(priceForNotional)
						.shift(order.baseAssetAmountFilled.precision.neg());
					return notional.toNotional();
				})()
			: filledTextBase;

	const handleHeaderMarketClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		handleClickMarket(order.marketIndex, order.marketType);
	};

	const handleToggleSizeDisplayType = (
		e: React.MouseEvent<HTMLButtonElement>
	) => {
		e.stopPropagation();
		onToggleSizeDisplayType();
	};

	const triggerContent = triggerPriceToUse.eqZero()
		? '-'
		: `${
				triggerConditionAbove ? 'Above' : 'Below'
			} ${triggerPriceToUse.toNotional(true)}`;

	const sizeDisplayTypeToggleLabel = (
		<div className="flex items-center gap-x-xs">
			<MobileTableCard.Label>
				{sizeDisplayType === 'notional' ? 'Value' : 'Size'}
			</MobileTableCard.Label>
			{!orderIsFullPosition && (
				<IconButton
					size="sm"
					variant="tertiary"
					className="-ml-xl pl-xl"
					onClick={handleToggleSizeDisplayType}
					icon={Swap}
				/>
			)}
		</div>
	);

	return (
		<MobileTableCard.Root
			expanded={isExpanded}
			setExpanded={handleExpandedChange}
		>
			<MobileTableCard.CollapsibleHeader expanded={isExpanded}>
				<div className="flex w-full items-center justify-between">
					<div className="flex flex-col gap-y-xs">
						<span
							className="typo-t2 text-text-default"
							onClick={handleHeaderMarketClick}
						>
							{marketName}
						</span>

						<div className="flex gap-x-xs">
							<Tag
								className={
									isLong
										? 'bg-text-positive-green-button/20 text-text-positive-green-button'
										: 'bg-text-negative-red-button/20 text-text-negative-red-button'
								}
							>
								{order.reduceOnly
									? `Close ${isLong ? 'Short' : 'Long'}`
									: isLong
										? 'Long'
										: 'Short'}
							</Tag>
							<Tag className="bg-button-secondary-bg text-text-default">
								{COMMON_UI_UTILS.getOrderLabelFromOrderDetails(order)}
							</Tag>
						</div>
					</div>
					<div
						className="flex flex-col items-end gap-y-xs"
						data-collapsible-card-fade
					>
						{sizeDisplayTypeToggleLabel}
						<span className="typo-t2 whitespace-nowrap">{sizeText}</span>
					</div>
				</div>
			</MobileTableCard.CollapsibleHeader>
			<MobileTableCard.CollapsibleContent>
				<div className="flex flex-col gap-y-lg">
					<div className="grid w-full grid-cols-2 gap-lg">
						<div className="flex flex-col gap-y-xs">
							{sizeDisplayTypeToggleLabel}
							<span className="typo-t2">{sizeText}</span>
						</div>
						<div className="flex flex-col items-end gap-y-xs">
							<MobileTableCard.Label>Filled</MobileTableCard.Label>
							<span className="typo-t2">{filledText}</span>
						</div>
					</div>
					<div className="grid grid-cols-3 gap-lg">
						<div className="flex flex-col gap-y-xs">
							<MobileTableCard.Label>Flag</MobileTableCard.Label>
							<span className="typo-t4">
								{order.postOnly && order.reduceOnly
									? 'Post & Reduce only'
									: order.postOnly
										? 'Post only'
										: order.reduceOnly
											? 'Reduce only'
											: '-'}
							</span>
						</div>
						<div className="flex flex-col items-center gap-y-xs">
							<MobileTableCard.Label>Price</MobileTableCard.Label>
							<div className="typo-t4">
								{ENUM_UTILS.match(order.orderType, OrderType.MARKET) ||
								ENUM_UTILS.match(order.orderType, OrderType.TRIGGER_MARKET) ? (
									'Market'
								) : limitPriceToUse.eqZero() ? (
									'-'
								) : (
									<NumberDisplay
										value={limitPriceToUse}
										type="currency"
										decimals={tickSizeDecimalPlaces}
										trimZeroes
									/>
								)}
							</div>
						</div>
						<div className="flex flex-col items-end gap-y-xs">
							<MobileTableCard.Label>Trigger Condition</MobileTableCard.Label>
							<span className="typo-t4">{triggerContent}</span>
						</div>
					</div>
					<div className="mt-0 flex w-full items-center gap-x-sm">
						{shouldShowEditButton && (
							<Button
								size="sm"
								variant="secondary"
								className="flex-1"
								onClick={handleShowEditOrderModal}
							>
								Edit
							</Button>
						)}
						<Button
							size="sm"
							variant="secondary"
							className="flex-1"
							onClick={() => {
								const toastId = `order-mobile-card-cancel-order-${
									order.orderId
								}-${Date.now()}`;
								cancelOrders(toastId, order.orderId);
							}}
						>
							Cancel
						</Button>
					</div>
				</div>
			</MobileTableCard.CollapsibleContent>
		</MobileTableCard.Root>
	);
};
